import axios, { type CancelToken } from "axios";
import { defineStore } from "pinia";

import {
  type AnstalldOrderByColumnType,
  type AnstalldaFilterKategoriType,
  type IAnstalldaAtArbetsgivareResponse,
  type IAvtalResponse,
  type IForetagResponse,
  type IInfoStatus,
  type INyckeltalForArbetsgivareResponse,
  KundserviceAppApiClient,
  KundserviceAppApiClientExceptions,
  type PensionsplanType,
  PlatformResponse_1OfAvtalResponse,
  type SortOrderType,
  ValidationProblemDetails,
} from "@/composables/KundserviceAppApiClient";
import { useNavigation } from "@/composables/useNavigation";
import type { ApiError } from "@/types/ApiError";
import { orgNumberWithDash } from "@/common/formatters";

import type {
  BasuppgifterSectionState,
  HeaderSectionState,
} from "./sectionDefinitions";
import { openNewTab } from "@/common/utils/openNewTab";

export interface FetchAnstalldaQuery {
  kategori?: AnstalldaFilterKategoriType;
  pensionsPlan?: PensionsplanType;
  orderBy?: AnstalldOrderByColumnType;
  sortOrder?: SortOrderType;
  top?: number;
  skip?: number;
}

export type State = {
  arbetsgivare: ArbetsgivareState;
  nyckeltal: NyckeltalState;
  anstallda: AnstalldaState;
  avtal: AvtalState;
};

type NyckeltalState = {
  isLoading: boolean;
  data: INyckeltalForArbetsgivareResponse | null;
  error: ValidationProblemDetails | null;
  infoStatus: IInfoStatus[];
  lastFetched: Date | null;
};

type ArbetsgivareState = {
  isLoading: boolean;
  foretag: IForetagResponse | null;
  error: ApiError | null;
  lastFetched: Date | null;
};

type AnstalldaState = {
  isLoading: boolean;
  data: IAnstalldaAtArbetsgivareResponse | null;
  infoStatus: IInfoStatus[];
  error: ValidationProblemDetails | null;
  lastFetched: Date | null;
};

type AvtalState = {
  isLoading: boolean;
  data: IAvtalResponse | null;
  infoStatus: IInfoStatus[];
  error: ValidationProblemDetails | null;
  lastFetched: Date | null;
};

export const useArbetsgivareStore = defineStore("arbetsgivare", {
  getters: {
    headerSection: (state) => createArbetsgivareHeaderSectionState(state),
    basuppgifterSection: (state) => createBasuppgifterSectionState(state),
    hasFetchedNyckeltal: (state) => state.nyckeltal.lastFetched !== null,
    hasFetchedAnstallda: (state) => state.anstallda.lastFetched !== null,
    canCreateKundkontakt: (state) =>
      !state.arbetsgivare.isLoading &&
      !state.arbetsgivare.error &&
      !!state.arbetsgivare.foretag,
  },
  state: (): State => {
    return {
      arbetsgivare: {
        isLoading: false,
        foretag: null,
        error: null,
        lastFetched: null,
      },
      nyckeltal: {
        isLoading: false,
        data: null,
        error: null,
        infoStatus: [],
        lastFetched: null,
      },
      anstallda: {
        isLoading: false,
        data: null,
        infoStatus: [],
        error: null,
        lastFetched: null,
      },
      avtal: {
        isLoading: false,
        data: null,
        infoStatus: [],
        error: null,
        lastFetched: null,
      },
    };
  },
  actions: {
    async fetchAnstallda(
      apiClient: KundserviceAppApiClient,
      query: FetchAnstalldaQuery | undefined = undefined,
      cancelToken: CancelToken | undefined = undefined,
    ) {
      if (!this.arbetsgivare.foretag?.id) {
        return;
      }

      this.$patch((store) => {
        store.anstallda.isLoading = true;
        store.anstallda.error = null;
        store.anstallda.lastFetched = new Date();
        store.anstallda.infoStatus = [];
      });

      try {
        const anstalldaResponse = await apiClient.getAnstalldaAtArbetsgivare(
          this.arbetsgivare.foretag.id,
          query?.kategori,
          query?.pensionsPlan,
          query?.orderBy,
          query?.sortOrder,
          query?.top,
          query?.skip,
          cancelToken,
        );

        if (!anstalldaResponse?.data?.arbetsgivareId) {
          this.anstallda.data = null;
          this.anstallda.infoStatus = anstalldaResponse?.statuses ?? [];
          return;
        }
        this.anstallda.data = anstalldaResponse.data ?? null;
        this.anstallda.isLoading = false;
        this.anstallda.infoStatus = anstalldaResponse?.statuses ?? [];
      } catch (e) {
        if (axios.isCancel(e)) {
          this.anstallda.data = null;
          throw new Error("Canceled");
        }
        this.$patch((store) => {
          if (e instanceof ValidationProblemDetails) {
            store.anstallda.error = e;
          }

          store.anstallda.data = null;
        });
      }
      this.anstallda.isLoading = false;
    },
    async fetchArbetsgivare(
      organisationsnummer: string,
      apiClient: KundserviceAppApiClient,
    ) {
      this.$patch((store) => {
        store.nyckeltal.lastFetched = null;
        store.arbetsgivare.isLoading = true;
        store.arbetsgivare.error = null;
        store.arbetsgivare.foretag = null;
        store.arbetsgivare.lastFetched = new Date();
      });

      const navigation = useNavigation();

      try {
        const foretagResponse = await apiClient.getForetag(organisationsnummer);

        if (foretagResponse === null) {
          this.$patch((store) => {
            store.arbetsgivare.foretag = null;
            store.arbetsgivare.isLoading = false;
          });

          navigation.notFound(organisationsnummer);

          return;
        }

        this.$patch((store) => {
          store.arbetsgivare.foretag = foretagResponse;
        });
        
        if(foretagResponse.crmUrl) {
          openNewTab(foretagResponse.crmUrl, true);
        }
      } catch (e) {
        this.$patch((store) => {
          if (e instanceof ValidationProblemDetails) {
            navigation.notFound(organisationsnummer);
          }
          if (e instanceof KundserviceAppApiClientExceptions) {
            store.arbetsgivare.error = {
              title: e.name,
              errors: e.message,
              status: e.status,
              detail: e.response,
            };
          }

          store.arbetsgivare.foretag = null;
        });
      }

      this.arbetsgivare.isLoading = false;
    },
    async fetchNyckeltal(apiClient: KundserviceAppApiClient) {
      if (!this.arbetsgivare.foretag?.id) {
        return;
      }
      this.$patch((store) => {
        store.nyckeltal.isLoading = true;
        store.nyckeltal.error = null;
        store.nyckeltal.data = null;
        store.nyckeltal.lastFetched = new Date();
      });

      try {
        const nyckelTalResponse = await apiClient.getNyckeltalForArbetsgivare(
          this.arbetsgivare.foretag.id,
        );

        this.$patch((state) => {
          state.nyckeltal.data =
            nyckelTalResponse.data as INyckeltalForArbetsgivareResponse;
          state.nyckeltal.infoStatus = nyckelTalResponse.statuses ?? [];
        });
      } catch (error) {
        this.$patch((state) => {
          if (error instanceof ValidationProblemDetails) {
            state.nyckeltal.error = error;
          }
          state.nyckeltal.data = null;
        });
      }
      this.nyckeltal.isLoading = false;
    },
    async fetchAvtal(apiClient: KundserviceAppApiClient) {
      if (!this.arbetsgivare.foretag?.id) {
        return;
      }
      this.onBeforeGetAvtalForArbetsgivare();

      try {
        const response = await apiClient.getAvtalForArbetsgivare(
          this.arbetsgivare.foretag.id,
        );
        this.onGetAvtalForArbetsgivareSuccess(response);
      } catch (error) {
        this.onGetAvtaForArbetsgivareError(error);
      }
      this.onAfterGetAvtalForArbetsgivare();
    },
    onBeforeGetAvtalForArbetsgivare() {
      this.$patch((store) => {
        store.avtal.isLoading = true;
        store.avtal.error = null;
        store.avtal.data = null;
        store.avtal.lastFetched = new Date();
      });
    },
    onGetAvtalForArbetsgivareSuccess(
      response: PlatformResponse_1OfAvtalResponse,
    ) {
      this.$patch((store) => {
        store.avtal.data = response.data ?? null;
      });
    },
    onGetAvtaForArbetsgivareError(error: unknown) {
      this.$patch((state) => {
        if (error instanceof ValidationProblemDetails) {
          state.avtal.error = error;
        }
        state.avtal.data = null;
      });
    },
    onAfterGetAvtalForArbetsgivare() {
      this.$patch((store) => {
        store.avtal.isLoading = false;
      });
    },
  },
});

function createArbetsgivareHeaderSectionState(
  state: State,
): HeaderSectionState | null {
  if (
    state.arbetsgivare.error ||
    state.arbetsgivare.isLoading ||
    !state.arbetsgivare.foretag
  ) {
    return null;
  }
  return {
    foretagNamn: state.arbetsgivare.foretag.namn ?? undefined,
    organisationsnummer: orgNumberWithDash(
      state.arbetsgivare.foretag?.organisationsnummer,
    ),
  };
}

function createBasuppgifterSectionState(
  state: State,
): BasuppgifterSectionState {
  if (state.arbetsgivare.isLoading || state.nyckeltal.isLoading) {
    return {
      isLoading: true,
      infoStatus: [],
      data: null,
      error: null,
    };
  }

  return {
    isLoading: false,
    infoStatus: state.nyckeltal.infoStatus,
    data: state.nyckeltal.data,
    error: state.nyckeltal.error,
  };
}
